import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Sparkles, Star, Zap, Crown, Lock, Unlock, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { TypewriterText } from "./TypewriterText";
const heroBg = "/images/hero-bg-optimized.webp";
import { useRef, lazy, Suspense } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

const AdminProjectButtons = lazy(() => import("./hero/AdminProjectButtons"));

export default function HeroSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const isMobile = useIsMobile();

  const { scrollYProgress } = useScroll(
    isMobile ? undefined : {
      target: sectionRef,
      offset: ["start start", "end start"],
    }
  );

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);

  const badgeItems = (
    <>
      <div className="shrink-0 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full bg-gradient-to-r from-primary/20 to-accent/10 border border-primary/30 shadow-lg shadow-primary/20">
        <Crown className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
        <span className="text-[10px] sm:text-xs text-primary font-bold tracking-wide-premium uppercase">Our MVP</span>
        <Star className="w-2 h-2 sm:w-3 sm:h-3 text-primary fill-primary" />
      </div>
      <div className="shrink-0 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full bg-card/40 border border-primary/15">
        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
        <span className="text-[10px] sm:text-xs text-foreground/90 font-bold tracking-wide-premium uppercase">Gen-Z Startup</span>
        <Zap className="w-2 h-2 sm:w-3 sm:h-3 text-accent fill-accent" />
      </div>
      <div className="shrink-0 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full bg-card/40 border border-primary/15">
        <Sparkles className="w-3 h-3 sm:w-4 sm:h-4 text-primary" />
        <span className="text-[10px] sm:text-xs text-foreground/90 font-bold tracking-wide-premium uppercase">Next-Gen Digital Solutions Agency</span>
        <Zap className="w-2 h-2 sm:w-3 sm:h-3 text-accent fill-accent" />
      </div>
      <div className="shrink-0 inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-1.5 sm:py-2.5 rounded-full bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/25">
        <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-accent" />
        <span className="text-[10px] sm:text-xs text-foreground/90 font-bold tracking-wide-premium uppercase">FearlessXCR7</span>
        <Star className="w-2 h-2 sm:w-3 sm:h-3 text-accent fill-accent" />
      </div>
    </>
  );

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 noise-overlay"
    >
      {/* Background */}
      <div className="absolute inset-0 z-0">
        {isMobile ? (
          <img
            src={heroBg}
            alt=""
            fetchPriority="high"
            decoding="async"
            width={1024}
            height={576}
            sizes="100vw"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <motion.div style={{ y }} className="absolute inset-0">
            <img
              src={heroBg}
              alt=""
              fetchPriority="high"
              decoding="async"
              width={1024}
              height={576}
              sizes="100vw"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        )}

        {/* Cinematic gradient overlay */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(160deg, 
              hsl(var(--background) / 0.95) 0%, 
              hsl(var(--background) / 0.85) 30%,
              hsl(var(--primary) / 0.07) 55%,
              hsl(var(--background) / 0.92) 80%,
              hsl(var(--background)) 100%
            )`,
          }}
        />

        {/* Morphing gradient blob */}
        {!isMobile && (
          <div
            className="absolute w-[600px] h-[600px] opacity-20 animate-morph-blob"
            style={{
              top: "10%",
              left: "50%",
              transform: "translateX(-50%)",
              background: `radial-gradient(circle, hsl(var(--primary) / 0.5) 0%, hsl(var(--accent) / 0.3) 40%, transparent 70%)`,
              filter: "blur(80px)",
            }}
          />
        )}

        {/* Cinematic light beams — desktop only */}
        {!isMobile && (
          <>
            <motion.div
              animate={{ x: [0, 80, 0], y: [0, -40, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -top-1/3 -left-1/4 w-[50%] h-[50%] rounded-full opacity-25"
              style={{
                background: "radial-gradient(circle, hsl(var(--primary) / 0.4) 0%, transparent 65%)",
                filter: "blur(120px)",
              }}
            />
            <motion.div
              animate={{ x: [0, -60, 0], y: [0, 60, 0], scale: [1, 0.85, 1] }}
              transition={{ duration: 30, repeat: Infinity, ease: "easeInOut" }}
              className="absolute -bottom-1/4 -right-1/4 w-[55%] h-[55%] rounded-full opacity-20"
              style={{
                background: "radial-gradient(circle, hsl(var(--accent) / 0.5) 0%, transparent 65%)",
                filter: "blur(140px)",
              }}
            />
            {/* Top spotlight */}
            <div 
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[80%] h-[40%] opacity-[0.07]"
              style={{
                background: "radial-gradient(ellipse 60% 40% at 50% 0%, hsl(var(--primary)), transparent)",
              }}
            />
          </>
        )}

        {/* Floating particles — enhanced */}
        {!isMobile && [0, 1, 2, 3, 4, 5].map((i) => (
          <motion.div
            key={i}
            animate={{
              opacity: [0, 0.8, 0],
              y: [0, -150 - i * 30, -300],
              x: [0, (i % 2 === 0 ? 20 : -20), 0],
            }}
            transition={{
              duration: 5 + i * 0.8,
              repeat: Infinity,
              delay: i * 0.9,
              ease: "easeInOut",
            }}
            className="absolute rounded-full"
            style={{
              left: `${10 + i * 15}%`,
              top: `${55 + (i % 3) * 12}%`,
              width: `${2 + (i % 3)}px`,
              height: `${2 + (i % 3)}px`,
              background: i % 2 === 0
                ? "hsl(var(--primary))"
                : "hsl(var(--accent))",
              boxShadow: i % 2 === 0
                ? "0 0 12px hsl(var(--primary) / 0.6)"
                : "0 0 12px hsl(var(--accent) / 0.6)",
            }}
          />
        ))}

        {/* Orbiting ring — desktop only */}
        {!isMobile && (
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] opacity-[0.06]"
            style={{
              border: "1px solid hsl(var(--primary) / 0.3)",
              borderRadius: "50%",
            }}
          >
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-primary shadow-lg shadow-primary/50"
            />
          </motion.div>
        )}

        {/* Final vignette */}
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-transparent to-background" />
      </div>

      {/* Animated Grid */}
      {!isMobile && (
        <motion.div
          animate={{ opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          className="absolute inset-0 grid-pattern"
        />
      )}

      {/* Scan line */}
      {!isMobile && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "200%" }}
            transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
            className="absolute top-1/3 h-[1px] w-1/3 bg-gradient-to-r from-transparent via-primary/40 to-transparent"
          />
          {/* Second vertical scan */}
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: "200%" }}
            transition={{ duration: 14, repeat: Infinity, ease: "linear", delay: 3 }}
            className="absolute left-2/3 w-[1px] h-1/4 bg-gradient-to-b from-transparent via-accent/20 to-transparent"
          />
        </div>
      )}

      {/* Content */}
      <motion.div 
        style={isMobile ? undefined : { opacity, scale }}
        className="section-container relative z-10 px-5 sm:px-6 lg:px-8"
      >
        <div className="flex items-start gap-6">
          {/* Left Side Project Buttons */}
          <div className="hidden md:flex flex-col gap-4 pt-32">
            {[
              { label: "Project A", href: "#projects", locked: true, status: null },
              { label: "Project X", href: "#project-x", locked: false, status: "Active" },
            ].map((item) => {
              const sharedClass = `flex items-center gap-2 px-5 py-2.5 rounded-xl border backdrop-blur-xl text-sm font-semibold transition-all duration-300 whitespace-nowrap ${
                item.locked
                  ? "border-muted/20 bg-card/20 text-muted-foreground opacity-50 cursor-not-allowed"
                  : "border-primary/25 bg-card/40 text-foreground hover:border-primary/60 hover:shadow-glow cursor-pointer"
              }`;
              const content = (
                <>
                  {item.locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5 text-primary" />}
                  {item.label}
                  {item.status === "Active" && (
                    <span className="flex items-center gap-1 text-[10px] text-green-400 font-medium ml-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 shadow-[0_0_6px_theme(colors.green.400)]" />
                      Active
                    </span>
                  )}
                </>
              );

              if (item.locked) {
                return <div key={item.label} className={sharedClass}>{content}</div>;
              }

              return (
                <motion.a
                  key={item.label}
                  href={item.href}
                  whileHover={{ scale: 1.06, x: 6 }}
                  whileTap={{ scale: 0.95 }}
                  className={sharedClass}
                >
                  {content}
                </motion.a>
              );
            })}
            <Suspense fallback={null}>
              <AdminProjectButtons />
            </Suspense>
          </div>

          {/* Main Content */}
          <div className="max-w-4xl mx-auto text-center flex-1">
            {/* Badges */}
            <div className="mt-6 sm:mt-8 mb-8 sm:mb-10 overflow-hidden relative" style={{ marginLeft: '-100%', marginRight: '-100%' }}>
              <div className="flex gap-3 sm:gap-4 animate-badge-scroll w-max">
                {badgeItems}
                {badgeItems}
                {!isMobile && badgeItems}
                {!isMobile && badgeItems}
              </div>
            </div>

            {/* Main Headline */}
            <div className="relative mb-10">
              {!isMobile && (
                <motion.div
                  animate={{ opacity: [0.3, 0.6, 0.3], scale: [1, 1.08, 1] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 blur-[100px]"
                  style={{
                    background: "radial-gradient(ellipse at center, hsl(var(--primary) / 0.4) 0%, hsl(var(--accent) / 0.2) 50%, transparent 70%)",
                  }}
                />
              )}
              <motion.h1
                initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-heading font-bold leading-[1.05] tracking-tight"
              >
                <span className="text-foreground/90">We're Making the</span>
                <br />
                <span className="relative inline-block">
                  <span className="text-gradient animate-text-glow">Impossible</span>
                  <motion.div
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 1, duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
                    className="absolute -bottom-2 left-0 right-0 h-[3px] bg-gradient-to-r from-primary via-accent to-primary rounded-full origin-left"
                  />
                </span>{" "}
                <span className="text-foreground">Possible</span>
              </motion.h1>
            </div>

            {/* Subheading */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto mb-10 sm:mb-14 tracking-premium leading-relaxed px-3 sm:px-1 overflow-hidden"
            >
              <TypewriterText
                text="We build powerful digital ecosystems where communities, automation, and systems work together. From structured Discord infrastructure to end-to-end community management, FearlessXCR7 is shaping scalable digital growth through Project X."
                speed={50}
                delay={800}
                highlightWords={[
                  { word: "powerful digital ecosystems", className: "text-primary font-semibold" },
                  { word: "Project X", className: "text-primary font-semibold" },
                ]}
              />
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 w-full sm:w-auto px-2 sm:px-0"
            >
              <motion.div
                whileHover={{ scale: 1.06, y: -3 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className="relative group"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-[#5865F2] to-[#7289DA] rounded-xl blur-lg opacity-50 group-hover:opacity-90 transition-opacity duration-300" />
                <Button variant="discord" size="xl" className="relative w-full sm:w-auto shadow-xl shadow-[#5865F2]/30 btn-3d" asChild>
                  <a href="https://discord.gg/EXEzefxAWn" target="_blank" rel="noopener noreferrer">
                    <MessageCircle className="w-5 h-5" />
                    Join our server
                  </a>
                </Button>
              </motion.div>
              <motion.div
                whileHover={{ scale: 1.06, y: -3 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                className="relative group"
              >
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-accent rounded-xl blur-lg opacity-40 group-hover:opacity-80 transition-opacity duration-300" />
                <Button variant="glow" size="xl" className="relative w-full sm:w-auto shadow-xl shadow-primary/30 btn-3d btn-gradient-animate" asChild>
                  <a href="#contact">
                    Get a Quote
                    <ArrowRight className="w-5 h-5" />
                  </a>
                </Button>
              </motion.div>
            </motion.div>

            {/* Trust Indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.8 }}
              className="mt-12 sm:mt-16 md:mt-20 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-5 px-2 sm:px-0"
            >
              {[
                { label: "Clean System Design", icon: "🎯", color: "primary" },
                { label: "Client-Focused", icon: "💎", color: "accent" },
                { label: "Scalable Solutions", icon: "🚀", color: "primary" },
              ].map((item, i) => (
                <motion.div
                  key={item.label}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 + i * 0.15 }}
                  whileHover={{ scale: 1.05, y: -4, boxShadow: "0 12px 40px hsl(var(--primary) / 0.15)" }}
                  className="flex items-center gap-2 sm:gap-3 px-5 sm:px-6 py-3 rounded-xl sm:rounded-2xl glass-premium cursor-default border border-primary/10 transition-all duration-300 w-full sm:w-auto justify-center"
                >
                  <span className="text-lg sm:text-xl">{item.icon}</span>
                  <div
                    className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${item.color === "primary" ? "bg-primary shadow-lg shadow-primary/40" : "bg-accent shadow-lg shadow-accent/40"}`}
                  />
                  <span className="text-xs sm:text-sm font-medium tracking-premium text-foreground/85">{item.label}</span>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <div className="flex flex-col items-center gap-2 animate-bounce" style={{ animationDuration: '1.5s' }}>
          <span className="text-[10px] text-muted-foreground/60 tracking-[0.2em] uppercase font-medium">Scroll</span>
          <div className="w-6 h-10 rounded-full border border-primary/30 flex items-start justify-center p-2 backdrop-blur-sm bg-background/10">
            <motion.div
              animate={{ y: [0, 8, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
              className="w-1 h-2.5 rounded-full bg-gradient-to-b from-primary to-accent"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
